import { Box, Icon, Typography, useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserRoleEnum, useUserContext } from '../../../contexts';

interface IBottomNavItem {
  icon: string;
  label: string;
  path: string;
  large?: boolean;
}

export const BottomNav: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { userRoles } = useUserContext();

  const isEmployee = userRoles.includes(UserRoleEnum.Employee);
  const isBA = userRoles.includes(UserRoleEnum.BusinessAdmin);
  const isReceptionist = userRoles.includes(UserRoleEnum.Receptionist);

  // Receptionist has access to both employee area and business area
  const hasRoles = isEmployee || isBA || isReceptionist;

  // Only show for users with roles
  if (!hasRoles) return null;

  // Build 5 slots — slot 2 (center) is always Início
  const slots: (IBottomNavItem | null)[] = [null, null, null, null, null];

  // Slot 0: Notificações (all roles)
  if (hasRoles) {
    slots[0] = { icon: 'notifications', label: 'Notificações', path: '/notifications' };
  }

  // Slot 1: primary section
  if (isBA && isEmployee) {
    // BA+Employee: show Profissional first
    slots[1] = { icon: 'engineering', label: 'Profissional', path: '/employee' };
  } else if (isEmployee) {
    slots[1] = { icon: 'engineering', label: 'Profissional', path: '/employee' };
  } else if (isBA) {
    slots[1] = { icon: 'apartment', label: 'Estabelecimento', path: '/business' };
  } else if (isReceptionist && !isEmployee) {
    // Pure receptionist: Estabelecimento as primary
    slots[1] = { icon: 'apartment', label: 'Estabelecimento', path: '/business' };
  }

  // Slot 2: INÍCIO (always center, larger)
  slots[2] = { icon: 'home', label: 'Início', path: '/home', large: true };

  // Slot 3+4: depends on role combination
  if (isBA && isEmployee) {
    // BA+Employee: 5 items
    slots[3] = { icon: 'apartment', label: 'Estabelecimento', path: '/business' };
    slots[4] = { icon: 'calendar_month', label: 'Agenda', path: '/schedule' };
  } else if (isReceptionist && isEmployee) {
    // Receptionist+Employee: both employee and business areas
    slots[3] = { icon: 'apartment', label: 'Estabelecimento', path: '/business' };
    slots[4] = { icon: 'calendar_month', label: 'Agenda', path: '/schedule' };
  } else if (isReceptionist && !isEmployee) {
    // Pure receptionist: Estabelecimento in slot 1, Agenda in slot 3
    slots[3] = { icon: 'calendar_month', label: 'Agenda', path: '/schedule' };
    // slot 4 stays null
  } else if (hasRoles) {
    // Single role: 4 items, Agenda in slot 3
    slots[3] = { icon: 'calendar_month', label: 'Agenda', path: '/schedule' };
    // slot 4 stays null
  }

  const isActive = (path: string) => {
    if (path === '/home') return location.pathname === '/home';
    return location.pathname.startsWith(path);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        height: 64,
        px: 1,
        pb: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
      }}
    >
      {slots.map((item, index) => {
        if (!item) {
          // Empty slot — keep flex space balanced
          return <Box key={`empty-${index}`} sx={{ flex: 1 }} />;
        }

        const active = isActive(item.path);
        const isCenter = item.large;

        return (
          <Box
            key={item.path}
            onClick={() => navigate(item.path)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.25,
              flex: isCenter ? 1.2 : 1,
              py: 0.5,
              cursor: 'pointer',
              color: active ? '#ec407a' : theme.palette.text.secondary,
              transition: 'all 0.15s ease',
              position: 'relative',
              '&:hover': {
                color: active ? '#ec407a' : theme.palette.text.primary,
              },
            }}
          >
            <Icon
              sx={{
                fontSize: isCenter ? 32 : 24,
                transition: 'all 0.15s ease',
              }}
            >
              {item.icon}
            </Icon>
            <Typography
              variant='caption'
              sx={{
                fontSize: isCenter ? '0.7rem' : '0.65rem',
                fontWeight: active ? 700 : 500,
                lineHeight: 1.1,
                maxWidth: 64,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
